import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import config from '../config';
import { useToast } from '@chakra-ui/react';
const AuthContext = createContext(null);

async function fetchCurrentUser() {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No auth token');
  const res = await fetch(`${config.apiUrl}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error('Failed to fetch user');
  }
  return res.json();
}

async function resendVerificationToken() {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No auth token');

  const res = await fetch(`${config.apiUrl}/auth/resend`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error('Failed to resend');
  }

  return res.json();
}

export const useAuthQuery = (onError, refetchInterval) => {
  const {
    data: user,
    isLoading: loading,
    refetch: refetchUser,
  } = useQuery('currentUser', fetchCurrentUser, {
    enabled: !!localStorage.getItem('token'),
    refetchInterval,
    onError: () => {
      // token invalid: clear session but stay on the page
      onError();
    },
    refetchOnWindowFocus: true,
  });

  return { user, loading, refetchUser };
};

export const AuthProvider = ({ children }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refetchInterval, setRefetchInterval] = useState(1000 * 30);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    queryClient.removeQueries('currentUser');
    window.dispatchEvent(new CustomEvent('user-logout'));
    navigate('/');
  };

  // react-query handles loading & refetch under the hood
  const { user, loading, refetchUser } = useAuthQuery(logout, refetchInterval);
  // login: store token + user, seed the cache
  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('login', userData);
    queryClient.setQueryData('currentUser', {
      ...userData,
      subscription_type: userData?.subscriptionType ?? userData?.subscription_type,
      premium:
        userData?.subscriptionType === 'premium' || userData?.subscription_type === 'premium',
      is_verified: userData?.isVerified ?? userData?.is_verified,
      token,
    });
    window.dispatchEvent(new CustomEvent('user-login', { detail: { user: userData } }));
    navigate('/'); // or wherever
  };

  useEffect(() => {
    if (user?.is_verified) {
      setRefetchInterval(1000 * 60 * 5);
    }
  }, [user]);
  // subscription mutation
  const { mutateAsync: updateSubscription } = useMutation(
    async (subscriptionType) => {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiUrl}/user/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription: subscriptionType }),
      });
      if (!res.headers.get('content-type')?.includes('application/json')) {
        throw new Error('Server returned invalid response');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update subscription');
      return data.user;
    },
    {
      onSuccess: (updatedUser) => {
        // merge and update cache + localStorage
        const token = localStorage.getItem('token');
        const newUser = { ...updatedUser, token };
        queryClient.setQueryData('currentUser', newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
      },
    },
  );

  const { mutate: resendVerification, isLoading: isLoadingResend } = useMutation(
    resendVerificationToken,
    {
      onSuccess: (data) => {
        toast({
          description: data?.message || 'Verification email resent',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      },
      onError: () => {
        toast({
          description: 'Something went wrong',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      },
    },
  );

  // logout: clear everything
  const value = {
    user: user && {
      ...user,
      ...(user && {
        subscription: user.subscription_type,
        premium: user.subscription_type === 'premium',
        subscriptionType: user.subscriptionType ?? user.subscription_type,
      }),
    },
    currentUser: user && {
      ...user,
      ...(user && {
        subscription: user.subscription_type,
        premium: user.subscription_type === 'premium',
        subscriptionType: user.subscriptionType ?? user.subscription_type,
      }),
    },
    loading,
    login,
    logout,
    updateSubscription,
    refetchUser,
    resendVerification,
    isLoadingResend,
  };
  // only render children once we've tried to load
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};
